/**
 * PromptManager
 *
 * A small, reusable helper for showing confirm/alert prompts inside a Shadow
 * DOM — a sibling to {@link ModalManager}. Whereas ModalManager drives the
 * component's own primary modals (and deliberately allows only one at a time,
 * sharing a single backdrop), PromptManager renders a lightweight prompt that
 * layers *on top of* whatever is currently on screen (including an open modal),
 * at a high z-index and with its own backdrop. This makes it suitable for the
 * increasingly common "are you sure?" pattern across web components without each
 * component re-implementing the markup, styling, focus handling and z-index.
 *
 * It is self-contained: the required styles are injected into the shadow root on
 * first use, so no per-component CSS or stylesheet adoption is needed.
 *
 * Usage:
 *   private prompts = new PromptManager(this.shadow);
 *   const ok = await this.prompts.confirm({
 *       title: 'Discard changes?',
 *       body: 'Your edits will be lost.',
 *       confirmLabel: 'Discard',
 *       cancelLabel: 'Keep editing',
 *       danger: true,
 *   });
 *   if (ok) { ... }
 *
 *   await this.prompts.alert({ title: 'Done', body: 'Saved.' });
 *
 *   // during disconnectedCallback:
 *   this.prompts.destroy();
 *
 * TODO (revisit): PromptManager is currently wired into eum-member-directory
 * (and the shared eum-user-toolbar it hosts). Other ModalManager consumers —
 * e.g. eum-dataroom-directory and the member-directory edit/confirm modals —
 * still hand-roll their own confirm dialogs and should be migrated to this
 * shared prompt pattern in a follow-up.
 */

const STYLE_MARKER = 'data-eum-prompt-styles';

export interface ConfirmOptions {
    /** Prompt heading. */
    title: string;
    /** Body copy. Plain text (rendered via textContent, never HTML). */
    body?: string;
    /** Confirm button label. Defaults to "OK". */
    confirmLabel?: string;
    /** Cancel button label. Defaults to "Cancel". */
    cancelLabel?: string;
    /** Style the confirm button as destructive (btn-danger) rather than primary. */
    danger?: boolean;
    /** Allow closing (as cancel) by clicking the backdrop. Defaults to true. */
    backdropDismiss?: boolean;
}

export interface AlertOptions {
    /** Prompt heading. */
    title: string;
    /** Body copy. Plain text (rendered via textContent, never HTML). */
    body?: string;
    /** Acknowledge button label. Defaults to "OK". */
    okLabel?: string;
}

export class PromptManager {
    private readonly shadow: ShadowRoot;
    private overlayEl: HTMLElement | null = null;
    private activeResolve: ((value: boolean) => void) | null = null;
    private activeBackdropDismiss = true;

    constructor(shadow: ShadowRoot) {
        this.shadow = shadow;
        document.addEventListener('keydown', this.onKeydown);
    }

    /** True while a prompt is currently displayed. */
    get isOpen(): boolean {
        return !!this.activeResolve;
    }

    /**
     * Show a confirmation prompt. Resolves `true` when confirmed, `false` when
     * cancelled (via the cancel button, the backdrop, or Escape).
     */
    confirm(options: ConfirmOptions): Promise<boolean> {
        return this.open({
            title: options.title,
            body: options.body,
            confirmLabel: options.confirmLabel ?? 'OK',
            cancelLabel: options.cancelLabel ?? 'Cancel',
            danger: !!options.danger,
            backdropDismiss: options.backdropDismiss ?? true,
            showCancel: true,
        });
    }

    /**
     * Show a single-button acknowledgement prompt. Resolves once dismissed.
     */
    alert(options: AlertOptions): Promise<void> {
        return this.open({
            title: options.title,
            body: options.body,
            confirmLabel: options.okLabel ?? 'OK',
            cancelLabel: '',
            danger: false,
            backdropDismiss: true,
            showCancel: false,
        }).then(() => undefined);
    }

    /** Remove listeners and any DOM. Call from `disconnectedCallback`. */
    destroy(): void {
        document.removeEventListener('keydown', this.onKeydown);
        this.settle(false);
        this.overlayEl?.remove();
        this.overlayEl = null;
    }

    // ── Internals ────────────────────────────────────────────────────────

    private open(config: {
        title: string;
        body?: string;
        confirmLabel: string;
        cancelLabel: string;
        danger: boolean;
        backdropDismiss: boolean;
        showCancel: boolean;
    }): Promise<boolean> {
        // Resolve any in-flight prompt as cancelled before showing a new one.
        this.settle(false);
        this.ensureStyles();

        const overlay = this.ensureOverlay();
        this.activeBackdropDismiss = config.backdropDismiss;

        const titleEl = overlay.querySelector('.eum-prompt-title') as HTMLElement;
        const bodyEl = overlay.querySelector('.eum-prompt-body') as HTMLElement;
        const confirmBtn = overlay.querySelector('.eum-prompt-confirm') as HTMLButtonElement;
        const cancelBtn = overlay.querySelector('.eum-prompt-cancel') as HTMLButtonElement;

        titleEl.textContent = config.title;
        bodyEl.textContent = config.body ?? '';
        bodyEl.hidden = !config.body;

        confirmBtn.textContent = config.confirmLabel;
        confirmBtn.className = `btn btn-sm eum-prompt-confirm ${config.danger ? 'btn-danger' : 'btn-primary'}`;

        cancelBtn.textContent = config.cancelLabel;
        cancelBtn.hidden = !config.showCancel;

        overlay.hidden = false;
        // Force a reflow so the fade-in transition plays.
        void overlay.offsetWidth;
        overlay.classList.add('show');

        // Focus the least-destructive action for keyboard users.
        (config.showCancel ? cancelBtn : confirmBtn).focus();

        return new Promise<boolean>((resolve) => {
            this.activeResolve = resolve;
        });
    }

    private ensureOverlay(): HTMLElement {
        if (this.overlayEl) return this.overlayEl;

        const overlay = document.createElement('div');
        overlay.className = 'eum-prompt-overlay';
        overlay.setAttribute('hidden', '');
        overlay.innerHTML = /*html*/ `
            <div class="eum-prompt-backdrop"></div>
            <div class="eum-prompt-dialog" role="alertdialog" aria-modal="true" aria-labelledby="eum-prompt-title">
                <div class="eum-prompt-content">
                    <h6 class="eum-prompt-title mb-2" id="eum-prompt-title"></h6>
                    <p class="eum-prompt-body text-body-secondary small mb-3"></p>
                    <div class="eum-prompt-actions d-flex justify-content-end gap-2">
                        <button type="button" class="btn btn-sm btn-outline-secondary eum-prompt-cancel"></button>
                        <button type="button" class="btn btn-sm btn-primary eum-prompt-confirm"></button>
                    </div>
                </div>
            </div>`;

        overlay.querySelector('.eum-prompt-confirm')?.addEventListener('click', () => this.settle(true));
        overlay.querySelector('.eum-prompt-cancel')?.addEventListener('click', () => this.settle(false));
        overlay.querySelector('.eum-prompt-backdrop')?.addEventListener('click', () => {
            if (this.activeBackdropDismiss) this.settle(false);
        });

        this.shadow.appendChild(overlay);
        this.overlayEl = overlay;
        return overlay;
    }

    /** Resolve the active prompt (if any) and hide the overlay. */
    private settle(result: boolean): void {
        const resolve = this.activeResolve;
        this.activeResolve = null;

        if (this.overlayEl) {
            this.overlayEl.classList.remove('show');
            this.overlayEl.setAttribute('hidden', '');
        }

        resolve?.(result);
    }

    private ensureStyles(): void {
        const root = this.shadow as unknown as { querySelector(sel: string): Element | null };
        if (root.querySelector(`style[${STYLE_MARKER}]`)) return;

        const style = document.createElement('style');
        style.setAttribute(STYLE_MARKER, '');
        style.textContent = `
.eum-prompt-overlay {
    position: fixed;
    inset: 0;
    z-index: 1080;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s ease;
}
.eum-prompt-overlay[hidden] { display: none; }
.eum-prompt-overlay.show { opacity: 1; }
.eum-prompt-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
}
.eum-prompt-dialog {
    position: relative;
    z-index: 1;
    max-width: 26rem;
    width: calc(100% - 3rem);
    background: var(--bs-modal-bg, #fff);
    color: var(--bs-body-color, #212529);
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.3);
}
.eum-prompt-content { padding: 1.25rem; }
.eum-prompt-body[hidden] { display: none; }
`;
        this.shadow.appendChild(style);
    }

    private readonly onKeydown = (e: KeyboardEvent): void => {
        if (!this.activeResolve) return;
        if (e.key === 'Escape') {
            e.preventDefault();
            this.settle(false);
        }
    };
}
